<template>
    <div class="py-6" id="billingAddressForm">
        <billing-address-form
            v-model="billingAddress"
            :primary-address="billingAddress"
            :enable-billing-form-toggle="false">
        </billing-address-form>

        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error list-disc text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"></span>:
                    <span class="font-normal" v-text="errorMsg"></span>
                </li>
            </ul>
        </div>

        <div class="w-full flex justify-around pt-4 pb-2">
            <div class="w-1/2 px-3">
                <button class="btn btn--secondary btn--full-width" @click="close"
                    v-text="$t('EDIT_BILLING_ADDRESS_FORM_BTN_CANCEL')"></button>
            </div>
            <div class="w-1/2 px-3">
                <submit-button
                    class="btn--full-width"
                    @click="submit"
                    :hoverable="false"
                    :processing="formProcessing"
                    v-text="$t('EDIT_BILLING_ADDRESS_FORM_BTN_UPDATE')"></submit-button>
            </div>
        </div>
    </div>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import PaymentService from '@/services/payments/PaymentService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import BillingAddressForm from '@/components/forms/addresses/BillingAddressForm';

export default {
    name: 'EditBillingAddressForm',
    data() {
        return {
            mappedAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            errorHandler: new ErrorResponseHandler(),
            formProcessing: false,
        };
    },
    props: {
        address: {
            required: true,
            type: Object,
        },
    },
    created() {
        this.mapAddressForAddressForm(this.address);
    },
    methods: {
        /**
         * Close the form.
         *
         * @returns {void}
         */
        close() {
            this.$emit('closed');
        },

        /**
         * Handle the save cta event.
         *
         * @returns {void}
         */
        submit() {
            this.toggleFormProcessing();
            this.validateInput();
        },

        /**
         * Handle updating the given address via the API.
         *
         * @returns {void}
         */
        handleUpdatingAddressViaApi() {
            let newAddress = {
                firstName: this.address.first_name,
                lastName: this.address.last_name,
                address1: this.mappedAddress.addressLine1,
                address2: this.mappedAddress.addressLine2,
                city: this.mappedAddress.city,
                country: this.address.country,
                postcode: this.mappedAddress.postcode,
            };

            let service = new PaymentService();
            service.updateBillingAddress(this.userId, this.address.id, newAddress)
                .then(() => {
                    setTimeout(() => this.$store.dispatch('address/fetchAllAddresses', true), 1000);

                    this.$scrollTo('#manageBllingContainer');
                    this.toggleFormProcessing();
                    this.close();
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response.data);
                    this.toggleFormProcessing();
                });
        },

        /**
         * Validate input before allowing user to update the address.
         *
         * @returns {void}
         */
        validateInput() {
            this.$v.$touch();

            this.areAllFormFieldsValid ? this.handleUpdatingAddressViaApi() : this.forceRequiredValidationMessagesVisibility();
        },

        /**
         * Handle mapping the address form for use in the delivery address form component.
         *
         * @param {*} address
         *
         * @returns {*}
         */
        mapAddressForAddressForm(address) {
            this.mappedAddress.addressLine1 = address.address1;
            this.mappedAddress.addressLine2 = address.address2;
            this.mappedAddress.city = address.city;
            this.mappedAddress.postcode = address.postcode;
        },

        /**
         * Force required validation message to be visible if user tries to submit an empty form.
         *
         * @returns {void}
         */
        forceRequiredValidationMessagesVisibility() {
            if (this.mappedAddress.address1 === null) this.mappedAddress.address1 = '';
            if (this.mappedAddress.city === null) this.mappedAddress.city = '';
            if (this.mappedAddress.postcode === null) this.mappedAddress.postcode = '';

            this.$scrollTo('#billingAddressForm');
            this.toggleFormProcessing();
        },

        /**
         * Toggle the form processing property.
         *
         * @returns {void}
         */
        toggleFormProcessing() {
            this.formProcessing = !this.formProcessing;
        },
    },
    computed: {
        billingAddress: {
            get() {
                return this.mappedAddress;
            },
            set(updatedAddress) {
                this.mappedAddress.addressLine1 = updatedAddress.addressLine1;
                this.mappedAddress.addressLine2 = updatedAddress.addressLine2;
                this.mappedAddress.city = updatedAddress.city;
                this.mappedAddress.postcode = updatedAddress.postcode;
            },
        },
        areAllFormFieldsValid() {
            return !this.$v.$invalid;
        },

        userId() {
            return this.$store.getters['account/getUserId'];
        },
    },
    validations: {
        mappedAddress: {
            addressLine1: {
                required,
            },
            city: {
                required,
            },
            postcode: {
                required,
            },
        },
    },
    components: {
        BillingAddressForm,
        SubmitButton,
    },
};
</script>
